import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FiClock, FiAlertCircle, FiCheckCircle, FiArrowLeft } from 'react-icons/fi';
import { getExamAttempt, saveExamAnswer, submitExam } from '../services/api';

// During an in-progress attempt the GET endpoint returns the attempt with questions
// (without correct answers). After submission it returns the full review payload.

export default function TrainingExam() {
  const { attemptId } = useParams();
  const [attempt, setAttempt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const tickRef = useRef(null);
  const submittedRef = useRef(false);

  // Load the attempt
  useEffect(() => {
    let mounted = true;
    getExamAttempt(attemptId)
      .then(res => {
        if (!mounted) return;
        setAttempt(res.data);
        // Pre-fill saved answers
        const saved = {};
        (res.data.questions || []).forEach(q => {
          if (q.user_choice) saved[q.id] = q.user_choice;
        });
        setAnswers(saved);
      })
      .catch(err => setError(err.response?.data?.error || err.message || 'Failed to load exam.'))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [attemptId]);

  // Countdown timer for in-progress attempts
  useEffect(() => {
    if (!attempt || attempt.status !== 'in_progress' || !attempt.deadline) return;
    const deadline = new Date(attempt.deadline + 'Z').getTime();
    const tick = () => {
      const remaining = Math.max(0, Math.floor((deadline - Date.now()) / 1000));
      setSecondsLeft(remaining);
      if (remaining === 0 && !submittedRef.current) {
        submittedRef.current = true;
        handleSubmit(true); // auto-submit at deadline
      }
    };
    tick();
    tickRef.current = setInterval(tick, 1000);
    return () => clearInterval(tickRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  const handlePick = async (q, letter) => {
    setAnswers(a => ({ ...a, [q.id]: letter }));
    // Auto-save in background
    try { await saveExamAnswer(attempt.id, q.id, letter); } catch (e) { /* keep going, server will get final answers on submit */ }
  };

  const handleSubmit = async (auto = false) => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    if (!auto) {
      const unanswered = (attempt.questions || []).filter(q => !answers[q.id]).length;
      const msg = unanswered > 0
        ? `You have ${unanswered} unanswered question${unanswered === 1 ? '' : 's'}. Submit anyway?`
        : 'Submit your exam? You won\'t be able to change answers after.';
      if (!window.confirm(msg)) {
        submittedRef.current = false;
        return;
      }
    }
    setSubmitting(true);
    try {
      await submitExam(attempt.id, answers);
      // Reload to get the graded review
      const res = await getExamAttempt(attemptId);
      setAttempt(res.data);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to submit exam.');
      submittedRef.current = false;
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div style={{ padding: 28 }}>Loading…</div>;
  if (error) return (
    <div style={{ padding: 28 }}>
      <Link to="/training" style={{ color: '#1e40af' }}>← Training</Link>
      <div style={{ background: '#fee2e2', color: '#991b1b', padding: 12, borderRadius: 8, marginTop: 12 }}>{error}</div>
    </div>
  );
  if (!attempt) return null;

  const isInProgress = attempt.status === 'in_progress';
  const questions = attempt.questions || [];
  const passed = attempt.passed;

  // Time formatting
  const mm = Math.floor(secondsLeft / 60).toString().padStart(2, '0');
  const ss = (secondsLeft % 60).toString().padStart(2, '0');
  const lowTime = secondsLeft <= 300; // < 5 min

  return (
    <div style={{ padding: '24px 28px', maxWidth: 880, margin: '0 auto' }}>
      {!isInProgress && (
        <Link
          to={`/training/modules/${attempt.module_id}`}
          style={{ color: '#1e40af', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 14, textDecoration: 'none' }}
        >
          <FiArrowLeft size={14} /> Back to module
        </Link>
      )}

      <div
        style={{
          marginTop: 12,
          padding: 20,
          background: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: 12,
          marginBottom: 20
        }}
      >
        <h1 style={{ margin: 0, color: '#0f172a', fontSize: 22 }}>{attempt.module_title}</h1>
        <div style={{ marginTop: 6, fontSize: 13, color: '#64748b' }}>
          Level Exam · {questions.length} questions · {attempt.module_track} Level {attempt.module_level}
        </div>

        {isInProgress ? (
          <div
            style={{
              marginTop: 14,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 14px',
              background: lowTime ? '#fee2e2' : '#dbeafe',
              border: `1px solid ${lowTime ? '#fca5a5' : '#93c5fd'}`,
              borderRadius: 8,
              color: lowTime ? '#991b1b' : '#1e3a8a',
              fontWeight: 600,
              fontSize: 16
            }}
          >
            <FiClock />
            <span>Time remaining: {mm}:{ss}</span>
            {lowTime && <span style={{ fontSize: 13, fontWeight: 500 }}>· auto-submit at 0:00</span>}
          </div>
        ) : (
          <div
            style={{
              marginTop: 14,
              padding: 14,
              background: passed ? '#dcfce7' : (attempt.status === 'expired' ? '#fef3c7' : '#fee2e2'),
              border: `1px solid ${passed ? '#86efac' : (attempt.status === 'expired' ? '#fcd34d' : '#fca5a5')}`,
              borderRadius: 8,
              color: passed ? '#14532d' : (attempt.status === 'expired' ? '#854d0e' : '#991b1b'),
              fontSize: 15,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 10
            }}
          >
            {passed ? <FiCheckCircle size={22} /> : <FiAlertCircle size={22} />}
            <div>
              {attempt.status === 'expired' && 'Exam expired (time ran out). '}
              Score: {attempt.score}% ({attempt.correct_count}/{attempt.total_count}) · {passed ? 'Passed' : 'Did not pass'}
              {passed && !attempt.advancement_approved_at && ' · awaiting admin advancement approval'}
              {attempt.advancement_approved_at && ` · advancement approved`}
            </div>
          </div>
        )}
      </div>

      {questions.map((q, qi) => {
        const userChoice = isInProgress ? answers[q.id] : q.user_choice;
        return (
          <div
            key={q.id}
            style={{
              padding: 16,
              background: 'white',
              border: '1px solid #e2e8f0',
              borderRadius: 10,
              marginBottom: 14
            }}
          >
            <div style={{ fontWeight: 600, color: '#0f172a', marginBottom: 12, lineHeight: 1.5 }}>
              {qi + 1}. {q.prompt}
            </div>
            {['A', 'B', 'C', 'D'].map(letter => {
              const text = q[`choice_${letter.toLowerCase()}`];
              if (!text) return null;
              const selected = userChoice === letter;
              const isCorrectChoice = !isInProgress && q.correct_choice === letter;
              const isUserChoice = !isInProgress && q.user_choice === letter;
              let bg = 'white';
              let border = '#e2e8f0';
              if (!isInProgress) {
                if (isCorrectChoice) {
                  bg = '#dcfce7'; border = '#86efac';
                } else if (isUserChoice && !q.is_correct) {
                  bg = '#fee2e2'; border = '#fca5a5';
                }
              } else if (selected) {
                bg = '#dbeafe'; border = '#93c5fd';
              }
              return (
                <label
                  key={letter}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 10,
                    padding: '8px 12px',
                    marginBottom: 6,
                    border: `1px solid ${border}`,
                    background: bg,
                    borderRadius: 6,
                    cursor: isInProgress ? 'pointer' : 'default'
                  }}
                >
                  <input
                    type="radio"
                    name={`q-${q.id}`}
                    disabled={!isInProgress}
                    checked={selected}
                    onChange={() => isInProgress && handlePick(q, letter)}
                    style={{ marginTop: 3 }}
                  />
                  <span style={{ flex: 1, fontSize: 14, lineHeight: 1.45 }}>
                    <strong style={{ marginRight: 6 }}>{letter}.</strong>{text}
                  </span>
                </label>
              );
            })}
            {!isInProgress && q.explanation && (
              <div
                style={{
                  marginTop: 10,
                  padding: 10,
                  background: q.is_correct ? '#f0fdf4' : '#fff7ed',
                  borderLeft: `4px solid ${q.is_correct ? '#16a34a' : '#f97316'}`,
                  borderRadius: 4,
                  fontSize: 13,
                  color: '#1f2937',
                  lineHeight: 1.5
                }}
              >
                <strong>{q.is_correct ? 'Correct.' : 'Correct answer was '}{!q.is_correct && q.correct_choice + '. '}</strong>
                {q.explanation}
              </div>
            )}
          </div>
        );
      })}

      {isInProgress && (
        <div
          style={{
            position: 'sticky',
            bottom: 0,
            background: 'white',
            borderTop: '1px solid #e2e8f0',
            padding: '14px 0',
            marginTop: 8,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 10
          }}
        >
          <div style={{ fontSize: 13, color: '#64748b' }}>
            Answered: {Object.keys(answers).length}/{questions.length}
          </div>
          <button
            disabled={submitting}
            onClick={() => handleSubmit(false)}
            style={{
              background: '#dc2626',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: 8,
              cursor: 'pointer',
              fontSize: 15,
              fontWeight: 600
            }}
          >
            {submitting ? 'Submitting…' : 'Submit exam'}
          </button>
        </div>
      )}
    </div>
  );
}
